import { PaginatedResource, RecipeCollectionPreview, RecipePreview } from '@/types';
import { MouseEventHandler, useEffect, useState } from 'react';
import { UI } from '@/Components/UI';
import { ButtonProps, LinkButtonProps } from '@/Components/UI/Button';
import { useApi, useAuth, useLocalizedRoute } from '@/hooks';
import { RecipeCollectionImage } from '@/Components/Specific';
import { Trans, useTranslation } from 'react-i18next';

type AddButtonProps = {
    recipe: RecipePreview
} & Omit<ButtonProps, 'onClick'>

export const AddButton = ({ recipe, ...rest }: AddButtonProps) => {
    const { localizedRoute } = useLocalizedRoute();

    const user = useAuth();
    const [isOpen, setIsOpen] = useState(false);

    const onClick: MouseEventHandler = (e) => {
        e.preventDefault();

        setIsOpen(true);
    };

    return user ? (
        <>
            <UI.Button
                {...rest}
                onClick={onClick}>
                <UI.Icon name={'collections_bookmark'} />
            </UI.Button>

            <UI.Dialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}>
                <AddButtonDialog recipe={recipe} />
            </UI.Dialog>
        </>
    ) : (
        <UI.Button.Link
            {...rest as LinkButtonProps}
            href={localizedRoute('login')}>
            <UI.Icon name={'collections_bookmark'} />
        </UI.Button.Link>
    )
        ;
};

type AddButtonDialogProps = {
    recipe: RecipePreview;
}

const AddButtonDialog = ({ recipe }: AddButtonDialogProps) => {
    const { t } = useTranslation();
    const { RecipeCollectionService } = useApi();

    const [recipeCollections, setRecipeCollections] = useState<PaginatedResource<RecipeCollectionPreview>>();

    const toggleCollection = (collection: RecipeCollectionPreview) => {
        const addedAt = collection.recipeAddedAt;

        if (addedAt) {
            // Remove the addedAt property to indicate the recipe is no longer in the collection, update state
            setRecipeCollections(prev => (prev ? {
                ...prev,
                data: prev?.data.map(c => c.slug === collection.slug ? { ...c, recipeAddedAt: undefined } : c)
            } : undefined));

            RecipeCollectionService.removeRecipeFromCollection(recipe.slug, collection.slug)
                .catch((error) => {
                    console.error('Failed to remove recipe from collection:', error);

                    // Revert the state change if the API call fails
                    setRecipeCollections(prev => (prev ? {
                        ...prev,
                        data: prev.data.map(c => c.slug === collection.slug ? { ...c, recipeAddedAt: addedAt } : c)
                    } : undefined));
                });
        } else {
            // Set the addedAt property to indicate the recipe is added to the collection, update state
            setRecipeCollections(prev => (prev ? {
                ...prev,
                data: prev?.data.map(c => c.slug === collection.slug ? {
                    ...c,
                    recipeAddedAt: new Date().toISOString()
                } : c)
            } : undefined));

            RecipeCollectionService.addRecipeToCollection(recipe.slug, collection.slug)
                .catch((error) => {
                    console.error('Failed to add recipe to collection:', error);

                    // Revert the state change if the API call fails
                    setRecipeCollections(prev => (prev ? {
                        ...prev,
                        data: prev.data.map(c => c.slug === collection.slug ? { ...c, recipeAddedAt: undefined } : c)
                    } : undefined));
                });
        }
    };

    useEffect(() => {
        RecipeCollectionService.listRecipeCollections(recipe.slug)
            .then((data) => {
                setRecipeCollections(data);
            })
            .catch((error) => {
                console.error('Failed to fetch recipe collections:', error);
            });
    }, []);

    // TODO PAGINATION
    return (
        <div className={'flex flex-col gap-1'}>
            {recipeCollections?.data.map((collection: RecipeCollectionPreview) => (
                <UI.Button
                    key={collection.slug}
                    buttonStyle={collection.recipeAddedAt ? 'filled' : 'tonal'}
                    className={'grid grid-cols-[auto_1fr] gap-2 ps-2'}
                    onClick={() => toggleCollection(collection)}>
                    <div className={'size-15 p-1 rounded-md bg-surface/40'}>
                        <RecipeCollectionImage
                            className={'gap-1'}
                            itemClassName={'rounded-sm'}
                            collection={collection} />
                    </div>

                    <div className={'flex flex-col justify-center text-start'}>
                        <div className={'font-serif font-medium text-lg'}>
                            {collection.title}
                        </div>

                        <div className={'text-sm'}>
                            <Trans
                                t={t}
                                i18nKey={'misc:createdAt'}
                                components={[
                                    <UI.Date value={new Date(collection.createdAt)} />
                                ]}/>
                        </div>
                    </div>
                </UI.Button>
            ))}
        </div>
    );
};
